import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { History, Search, Filter } from "lucide-react";
import api, { HR_BASE_URL } from "../api";

const Page = styled.div`
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
  animation: fadeIn 0.4s ease-out;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text);
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: var(--muted);
  font-size: 15px;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: var(--glass);
  backdrop-filter: blur(12px);
  padding: 24px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) => props.color || 'var(--text)'};
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: var(--muted);
  font-weight: 500;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  background: var(--glass);
  padding: 20px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  flex-wrap: wrap;
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0 12px;
  flex: 1;
  min-width: 250px;
  transition: all 0.2s;
  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const StyledInput = styled.input`
  border: none;
  background: transparent;
  padding: 12px;
  color: var(--text);
  width: 100%;
  font-size: 14px;
  &:focus { outline: none; }
  &::placeholder { color: var(--muted); }
`;

const StyledSelect = styled.select`
  background: var(--bg);
  border: 1px solid var(--border);
  color: var(--text);
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  min-width: 150px;
  &:focus { outline: none; border-color: var(--primary); }
`;

const TableContainer = styled.div`
  background: var(--glass);
  backdrop-filter: blur(12px);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.02);
`;

const Td = styled.td`
  padding: 16px 20px;
  font-size: 14px;
  color: var(--text);
  border-bottom: 1px solid var(--border);
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  ${(props) => props.status === 'Approved' ? `
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  ` : `
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
  `}
`;

function AdminLeaveHistory() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const res = await api.get(`${HR_BASE_URL}departments/`);
        setDepartments(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch departments", err);
      }
    };
    fetchDepts();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      let url = `${HR_BASE_URL}leaves/history/?`;
      if (statusFilter) url += `status=${statusFilter}&`;
      if (searchTerm) url += `employee_name=${searchTerm}&`;
      if (departmentFilter) url += `department=${departmentFilter}&`;
      if (fromDate) url += `from_date=${fromDate}&`;
      if (toDate) url += `to_date=${toDate}&`;
      
      const response = await api.get(url);
      setLeaves(response.data);
    } catch (err) {
      console.error("Error fetching history", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Adding a slight debounce for search could be better, but direct call is okay here
    const delayDebounce = setTimeout(() => {
      fetchHistory();
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, statusFilter, departmentFilter, fromDate, toDate]);

  const totalApproved = leaves.filter(l => l.status === 'Approved').length;
  const totalRejected = leaves.filter(l => l.status === 'Rejected').length;

  return (
    <Page>
      <Header>
        <div>
          <Title>
            <History size={32} color="var(--primary)" />
            Leave History
          </Title>
          <Subtitle>Comprehensive record of all processed employee leaves</Subtitle>
        </div>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatLabel>Total Approved Leaves</StatLabel>
          <StatValue color="#10b981">{totalApproved}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Total Rejected Leaves</StatLabel>
          <StatValue color="#ef4444">{totalRejected}</StatValue>
        </StatCard>
      </StatsGrid>

      <FiltersContainer>
        <InputGroup style={{ flex: '1 1 200px' }}>
          <Search size={18} color="var(--muted)" />
          <StyledInput 
            type="text" 
            placeholder="Search by Employee..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <StyledSelect 
          style={{ flex: '1 1 150px' }} 
          value={departmentFilter} 
          onChange={(e) => setDepartmentFilter(e.target.value)}
        >
          <option value="">All Departments</option>
          {departments.map(dept => (
            <option key={dept.id || dept._id} value={dept.name || dept.department_name}>
              {dept.name || dept.department_name}
            </option>
          ))}
        </StyledSelect>

        <InputGroup style={{ flex: '1 1 130px' }}>
          <StyledInput 
            type="date" 
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            title="From Date"
          />
        </InputGroup>

        <InputGroup style={{ flex: '1 1 130px' }}>
          <StyledInput 
            type="date" 
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            title="To Date"
          />
        </InputGroup>
        
        <StyledSelect style={{ flex: '1 1 120px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </StyledSelect>
      </FiltersContainer>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Req ID</Th>
              <Th>Employee</Th>
              <Th>Department</Th>
              <Th>Leave Type</Th>
              <Th>Dates</Th>
              <Th>Status</Th>
              <Th>Reviewed By</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><Td colSpan="7" style={{textAlign: 'center', padding: '40px'}}>Loading history...</Td></tr>
            ) : leaves.length > 0 ? (
              leaves.map(leave => (
                <tr key={leave.id}>
                  <Td>#{leave.id}</Td>
                  <Td>
                    <strong>{leave.employee_name || 'N/A'}</strong>
                    <div style={{fontSize: '12px', color: 'var(--muted)'}}>ID: {leave.employee_id}</div>
                  </Td>
                  <Td>{leave.department || 'N/A'}</Td>
                  <Td>{leave.leave_type}</Td>
                  <Td>
                    {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                  </Td>
                  <Td><StatusBadge status={leave.status}>{leave.status}</StatusBadge></Td>
                  <Td>{leave.reviewed_by_name || 'System'}</Td>
                </tr>
              ))
            ) : (
              <tr><Td colSpan="7" style={{textAlign: 'center', padding: '40px'}}>No leave records found.</Td></tr>
            )}
          </tbody>
        </Table>
      </TableContainer>
    </Page>
  );
}

export default AdminLeaveHistory;
